<template>
  <Loader v-if="loader" />
  <div class="card">
    <h3>Info Crediti</h3>

    <div class="card mb-4">
      <h4>Andamento Crediti per Anno</h4>
      <div class="chart-container">
        <Chart
          type="bar"
          :data="chartData"
          :options="chartOptions"
          ref="chart"
          @mousemove="onChartHover"
          @mouseleave="hideTooltip"
        />
        <div
          v-if="showCustomTooltip"
          class="custom-tooltip"
          :style="tooltipStyle"
        >
          <div class="tooltip-title">{{ tooltipData.title }}</div>
          <div class="tooltip-total">{{ tooltipData.total }}</div>
          <div class="tooltip-separator"></div>
          <div class="tooltip-content">
            <div
              v-for="(item, index) in tooltipData.items"
              :key="index"
              class="tooltip-row"
            >
              {{ item.agente }}: {{ item.total }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card" v-if="loaded_items">
      <div class="export-button-container mb-2">
        <Button
          label="Export in CSV"
          icon="pi pi-download"
          @click="exportCSV"
        />
      </div>

      <DataTable
        :value="items"
        tableStyle="w-full"
        v-model:filters="filters"
        v-model:expandedRows="expandedRows"
        dataKey="id"
        responsiveLayout="scroll"
        :sortField="'totale_saldo'"
        :sortOrder="-1"
      >
        <template #empty> Nessun risultato </template>

        <Column expander style="width: 3rem" />
        <Column field="name" header="Agente" />
        <Column field="totale_saldo" header="Totale Saldo" :sortable="true">
          <template #body="{ data }">
            {{ formatCurrency(data.totale_saldo) }}
          </template>
        </Column>

        <template #expansion="slotProps">
          <div class="p-3">
            <DataTable
              :value="slotProps.data.rappresentanti"
              v-model:expandedRows="expandedRowsRappresentanti"
              dataKey="id"
              class="mt-2"
              responsiveLayout="scroll"
              :sortField="'totale_saldo'"
              :sortOrder="-1"
            >
              <Column expander style="width: 3rem" />
              <Column field="name" header="Rappresentante" />
              <Column field="totale_saldo" header="Totale Saldo">
                <template #body="{ data }">
                  {{ formatCurrency(data.totale_saldo) }}
                </template>
              </Column>

              <template #expansion="slotProps2">
                <div class="p-3">
                  <DataTable
                    :value="slotProps2.data.clienti"
                    v-model:expandedRows="expandedRowsClienti"
                    dataKey="id"
                    class="mt-2"
                    responsiveLayout="scroll"
                    :sortField="'totale_saldo'"
                    :sortOrder="-1"
                  >
                    <Column expander style="width: 3rem" />
                    <Column
                      field="ragsoc"
                      header="Ragione Sociale"
                      :sortable="true"
                    />
                    <Column field="nome" header="Nome" :sortable="true" />
                    <Column field="cognome" header="Cognome" :sortable="true" />
                    <Column
                      field="cluster"
                      header="Tipologia"
                      :sortable="true"
                    />
                    <Column
                      field="totale_saldo"
                      header="Totale Saldo"
                      :sortable="true"
                    >
                      <template #body="{ data }">
                        {{ formatCurrency(data.totale_saldo) }}
                      </template>
                    </Column>

                    <template #expansion="slotProps3">
                      <div class="p-3">
                        <DataTable
                          :value="slotProps3.data.forniture"
                          :rows="10"
                          :paginator="true"
                          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                          :rowsPerPageOptions="[10, 25, 50]"
                          currentPageReportTemplate="Mostra {first} a {last} di {totalRecords}"
                          class="mt-2"
                          responsiveLayout="scroll"
                          :sortField="'saldo'"
                          :sortOrder="-1"
                        >
                          <Column field="tipo" header="Tipo" :sortable="true" />
                          <Column field="code" header="Codice" :sortable="true">
                            <template #body="{ data }">
                              {{ data.code }}
                            </template>
                          </Column>
                          <Column
                            field="indirizzo.indirizzo"
                            header="Indirizzo"
                          >
                            <template #body="{ data }">
                              {{
                                `${data.indirizzo.toponimo || ""} ${
                                  data.indirizzo.indirizzo || ""
                                } ${data.indirizzo.civico || ""}, ${
                                  data.indirizzo.cap || ""
                                } ${data.indirizzo.comune || ""}`
                              }}
                            </template>
                          </Column>
                          <Column
                            field="contatti.email"
                            header="Email"
                            :sortable="true"
                          />
                          <Column field="contatti.telefono" header="Telefono" />
                          <Column
                            field="contatti.cellulare"
                            header="Cellulare"
                          />
                          <Column field="saldo" header="Saldo" :sortable="true">
                            <template #body="{ data }">
                              {{ formatCurrency(data.saldo) }}
                            </template>
                          </Column>
                          <Column
                            field="data_fine"
                            header="Data Fine"
                            :sortable="true"
                          >
                            <template #body="{ data }">
                              {{ formatDate(data.data_fine) }}
                            </template>
                          </Column>
                        </DataTable>
                      </div>
                    </template>
                  </DataTable>
                </div>
              </template>
            </DataTable>
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
import Service from "@/services/ask-management/agenti-service";
import { ref, onMounted, getCurrentInstance } from "vue";
import Loader from "@/components/Loader.vue";
import moment from "moment";
import Chart from "primevue/chart";
import Button from "primevue/button";

export default {
  components: {
    Loader,
    Chart,
    Button,
  },
  setup() {
    const loader = ref(false);
    const loaded_items = ref(false);
    const self = getCurrentInstance();
    self.$http = self.appContext.config.globalProperties.$http;
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$http, self.$eventBus);
    const items = ref([]);

    const filters = ref({});
    const expandedRows = ref([]);
    const expandedRowsRappresentanti = ref([]);
    const expandedRowsClienti = ref([]);

    const chartData = ref({
      labels: [],
      datasets: [
        {
          label: "Totale Crediti",
          data: [],
          backgroundColor: ["rgba(54, 162, 235, 0.6)"],
          borderColor: ["rgba(54, 162, 235, 1)"],
          borderWidth: 1,
        },
      ],
    });

    const showCustomTooltip = ref(false);
    const tooltipStyle = ref({
      left: "0px",
      top: "0px",
    });
    const tooltipData = ref({
      title: "",
      total: "",
      items: [],
    });

    const chartOptions = ref({
      plugins: {
        tooltip: {
          enabled: false,
        },
        legend: {
          display: false,
        },
      },
      scales: {
        y: {
          ticks: {
            callback: function (value) {
              return value.toLocaleString("it-IT", {
                style: "currency",
                currency: "EUR",
              });
            },
          },
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    });

    const chart = ref(null);

    const updateChartData = (yearlyTotals) => {
      if (!yearlyTotals || Object.keys(yearlyTotals).length === 0) {
        chartData.value = {
          labels: [],
          datasets: [
            {
              label: "Totale Crediti",
              data: [],
              backgroundColor: "rgba(54, 162, 235, 0.6)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
            },
          ],
        };
        return;
      }

      const yearDetails = {};
      items.value.forEach((agente) => {
        agente.rappresentanti.forEach((rappresentante) => {
          rappresentante.clienti.forEach((cliente) => {
            cliente.forniture.forEach((fornitura) => {
              const year = moment(fornitura.data_fine).format("YYYY");
              if (!yearDetails[year]) {
                yearDetails[year] = [];
              }
              yearDetails[year].push({
                agente: agente.name,
                rappresentante: rappresentante.name,
                cliente:
                  `${cliente.ragsoc} ${cliente.nome} ${cliente.cognome}`.trim(),
                fornitura: fornitura.code,
                saldo: fornitura.saldo,
              });
            });
          });
        });
      });

      const sortedYears = Object.keys(yearlyTotals).sort();

      chartData.value = {
        labels: sortedYears,
        datasets: [
          {
            label: "Totale Crediti",
            data: sortedYears.map((year) => yearlyTotals[year]),
            backgroundColor: Array(sortedYears.length).fill(
              "rgba(54, 162, 235, 0.6)"
            ),
            borderColor: Array(sortedYears.length).fill(
              "rgba(54, 162, 235, 1)"
            ),
            borderWidth: 1,
            yearDetails: yearDetails,
          },
        ],
      };
    };

    const getData = async () => {
      loader.value = true;
      try {
        const response = await service.getCreditiUscitiInfo();

        if (response.items) {
          items.value = response.items.details;

          if (response.items.yearly_totals) {
            updateChartData(response.items.yearly_totals);
          } else {
            const yearlyTotals = {};
            items.value.forEach((agente) => {
              agente.rappresentanti.forEach((rappresentante) => {
                rappresentante.clienti.forEach((cliente) => {
                  cliente.forniture.forEach((fornitura) => {
                    const year = moment(fornitura.data_fine).format("YYYY");
                    yearlyTotals[year] =
                      (yearlyTotals[year] || 0) + fornitura.saldo;
                  });
                });
              });
            });
            updateChartData(yearlyTotals);
          }

          loaded_items.value = true;
        }
      } catch (error) {
        console.error("Errore nel caricamento dei dati:", error);
        items.value = [];
        updateChartData({});
      } finally {
        loader.value = false;
      }
    };

    const formatCurrency = (value) => {
      return value.toLocaleString("it-IT", {
        style: "currency",
        currency: "EUR",
      });
    };

    const formatDate = (value) => {
      return moment(value).format("DD/MM/YYYY");
    };

    const exportCSV = () => {
      let csvContent = [];

      csvContent.push(["RIEPILOGO TOTALI"]);
      csvContent.push([]);

      csvContent.push(["TOTALI PER AGENTE"]);
      csvContent.push(["Agente", "Totale Saldo"]);
      items.value.forEach((agente) => {
        csvContent.push([agente.name, formatCurrency(agente.totale_saldo)]);
      });
      csvContent.push([]);
      csvContent.push([]);

      csvContent.push(["TOTALI PER RAPPRESENTANTE"]);
      csvContent.push(["Agente", "Rappresentante", "Totale Saldo"]);
      items.value.forEach((agente) => {
        agente.rappresentanti.forEach((rappresentante) => {
          csvContent.push([
            agente.name,
            rappresentante.name,
            formatCurrency(rappresentante.totale_saldo),
          ]);
        });
      });
      csvContent.push([]);
      csvContent.push([]);

      csvContent.push(["TOTALI PER CLIENTE"]);
      csvContent.push(["Agente", "Rappresentante", "Cliente", "Totale Saldo"]);
      items.value.forEach((agente) => {
        agente.rappresentanti.forEach((rappresentante) => {
          rappresentante.clienti.forEach((cliente) => {
            csvContent.push([
              agente.name,
              rappresentante.name,
              `${cliente.ragsoc} ${cliente.nome} ${cliente.cognome}`.trim(),
              formatCurrency(cliente.totale_saldo),
            ]);
          });
        });
      });
      csvContent.push([]);
      csvContent.push([]);

      items.value.forEach((agente) => {
        csvContent.push([`DETTAGLIO AGENTE: ${agente.name}`]);
        csvContent.push([
          `Totale Saldo: ${formatCurrency(agente.totale_saldo)}`,
        ]);
        csvContent.push([]);

        agente.rappresentanti.forEach((rappresentante) => {
          csvContent.push([`RAPPRESENTANTE: ${rappresentante.name}`]);
          csvContent.push([
            `Totale Saldo: ${formatCurrency(rappresentante.totale_saldo)}`,
          ]);
          csvContent.push([]);

          csvContent.push([
            "Ragione Sociale",
            "Nome",
            "Cognome",
            "Tipologia",
            "Totale Saldo Cliente",
            "Tipo Fornitura",
            "Codice",
            "Indirizzo",
            "Email",
            "Telefono",
            "Cellulare",
            "Saldo Fornitura",
            "Data Fine",
          ]);

          rappresentante.clienti.forEach((cliente) => {
            cliente.forniture.forEach((fornitura) => {
              csvContent.push([
                cliente.ragsoc,
                cliente.nome,
                cliente.cognome,
                cliente.cluster,
                formatCurrency(cliente.totale_saldo),
                fornitura.tipo,
                fornitura.code,
                `${fornitura.indirizzo.toponimo || ""} ${
                  fornitura.indirizzo.indirizzo || ""
                } ${fornitura.indirizzo.civico || ""}, ${
                  fornitura.indirizzo.cap || ""
                } ${fornitura.indirizzo.comune || ""}`,
                fornitura.contatti.email,
                fornitura.contatti.telefono,
                fornitura.contatti.cellulare,
                formatCurrency(fornitura.saldo),
                moment(fornitura.data_fine).format("DD/MM/YYYY"),
              ]);
            });
          });
          csvContent.push([]);
        });
        csvContent.push([]);
        csvContent.push(["-------------------------------------------"]);
        csvContent.push([]);
      });

      const csv = csvContent
        .map((row) => {
          return row
            .map((cell) => {
              if (cell === undefined || cell === null) return "";
              return `"${String(cell).replace(/"/g, '""')}"`;
            })
            .join(",");
        })
        .join("\n");

      const blob = new Blob(["\ufeff" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `crediti_info.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const onChartHover = (event) => {
      const chartElement = chart.value;
      if (!chartElement) return;

      const chartInstance = chartElement.chart;

      const elements = chartInstance.getElementsAtEventForMode(
        event,
        "nearest",
        { intersect: true },
        false
      );

      if (elements.length > 0) {
        const firstPoint = elements[0];
        const dataset = chartData.value.datasets[firstPoint.datasetIndex];
        const value = dataset.data[firstPoint.index];
        const year = chartData.value.labels[firstPoint.index];
        const details = dataset.yearDetails[year];

        const agenteGroups = {};
        if (details) {
          details.forEach((detail) => {
            if (!agenteGroups[detail.agente]) {
              agenteGroups[detail.agente] = 0;
            }
            agenteGroups[detail.agente] += detail.saldo;
          });
        }

        tooltipData.value = {
          title: `Dettaglio Anno ${year}`,
          total: `Totale: ${value.toLocaleString("it-IT", {
            style: "currency",
            currency: "EUR",
          })}`,
          items: Object.entries(agenteGroups)
            .sort((a, b) => b[1] - a[1])
            .map(([agente, total]) => ({
              agente,
              total: total.toLocaleString("it-IT", {
                style: "currency",
                currency: "EUR",
              }),
            })),
        };

        tooltipStyle.value = {
          left: `${event.offsetX - 320}px`,
          top: `${event.offsetY + 10}px`,
        };

        showCustomTooltip.value = true;
      } else {
        showCustomTooltip.value = false;
      }
    };

    const hideTooltip = () => {
      showCustomTooltip.value = false;
    };

    onMounted(() => {
      getData();
    });

    return {
      items,
      loaded_items,
      loader,
      filters,
      formatCurrency,
      formatDate,
      expandedRows,
      expandedRowsRappresentanti,
      expandedRowsClienti,
      chartData,
      chartOptions,
      exportCSV,
      showCustomTooltip,
      tooltipStyle,
      tooltipData,
      chart,
      onChartHover,
      hideTooltip,
    };
  },
};
</script>

<style scoped>
.p-datatable .p-rowgroup-header {
  background: #f8f9fa;
}
.p-datatable .p-rowgroup-header-name {
  font-weight: 600;
}
.p-datatable .p-paginator {
  justify-content: flex-end;
  padding: 0.5rem;
  border-width: 0;
  background-color: transparent;
}

.p-datatable .p-paginator .p-paginator-current {
  margin-right: auto;
}

.p-datatable .p-paginator .p-dropdown {
  margin-left: 0.5rem;
}

.p-datatable .p-paginator button {
  margin: 0 0.2rem;
}

.p-datatable {
  margin-bottom: 1rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.p-paginator .p-paginator-pages button {
  min-width: 2.5rem;
  height: 2.5rem;
}

.p-paginator .p-dropdown {
  height: 2.5rem;
}

.p-paginator .p-paginator-current {
  color: #666;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.p-chart {
  height: 300px;
}

.card {
  background: #ffffff;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14),
    0 1px 3px 0 rgba(0, 0, 0, 0.12);
}

.p-chart {
  min-height: 300px;
  margin: 1rem 0;
}

.export-button-container {
  display: flex;
  justify-content: flex-end;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.chart-container {
  position: relative;
}

.custom-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  padding: 15px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 1000;
  width: 300px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

.tooltip-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 8px;
}

.tooltip-total {
  margin-bottom: 8px;
}

.tooltip-separator {
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin: 8px 0;
}

.tooltip-row {
  margin: 4px 0;
  white-space: nowrap;
}

.tooltip-content {
  overflow-y: auto;
  padding-right: 5px;
}
</style>

